import { Timescale } from '@opallabs/concepts'
import {
  getDayIndexFromDate,
  getWeekIndexFromDate,
  getIndexFromScrollOffset,
} from '../utils/timeFunctions'
import { WeekViewMode } from '../WeekView/types'
import {
  isIndexVisible,
  calculateLargestDayCount,
  getNearestDateWithData,
  DAYCOUNT,
  DAYFETCHBUFFER,
  DAYWIDTH,
  WEEKCOUNT,
  WEEKFETCHBUFFER,
  WEEKWIDTH,
} from './helpers'

export const actionTypes = {
  SET_ACTIVE_INDEX: 'SET_ACTIVE_INDEX',
  SET_DIMENSIONS: 'SET_DIMENSIONS',
  SET_DRAG_END: 'SET_DRAG_END',
  SET_DRAG_OFFSET: 'SET_DRAG_OFFSET',
  SET_DRAG_START: 'SET_DRAG_START',
  SET_FETCH_MARKERS: 'SET_FETCH_MARKERS',
  SET_HISTOGRAM_DATA: 'SET_HISTOGRAM_DATA',
  SET_SCROLL_OFFSET: 'SET_SCROLL_OFFSET',
  UPDATE_HISTOGRAM_DATA: 'UPDATE_HISTOGRAM_DATA',
}

// In Content mode the bars reflect content volume (posts_count) rather than
// moment volume. Content mode positions each post on its own scheduled_at, so
// aligning the histogram to posts matches what the user sees on the grid and
// supports the "where are the gaps?" gap-detection workflow. Falls back to
// moments_count when posts_count is absent (older server responses).
const buildHistogramData = (newData, oldData, resetData, viewMode) => {
  const histogramData = resetData ? {} : { ...oldData }
  const useContentCount = viewMode === WeekViewMode.Content
  newData.forEach((day) => {
    histogramData[day.date] = useContentCount
      ? (day.posts_count ?? day.moments_count)
      : day.moments_count
  })
  return histogramData
}

// Per-day count of content *launching* that day (by its own scheduled_at),
// each piece counted once no matter how many in-market days it spans. Drives
// the distinct-overlap week total so a 7-day span reads as 1, not 7, without
// disturbing the in-market bar heights. Content mode only.
const buildLaunchingData = (newData, oldData, resetData, viewMode) => {
  if (viewMode !== WeekViewMode.Content) return {}
  const launchingData = resetData ? {} : { ...oldData }
  newData.forEach((day) => {
    launchingData[day.date] = day.launching_count ?? 0
  })
  return launchingData
}

export const reducer = (state, action) => {
  switch (action.type) {
    case actionTypes.SET_ACTIVE_INDEX: {
      const UNITCOUNT =
        action.payload.mode === Timescale.Day ? DAYCOUNT : WEEKCOUNT
      const UNITWIDTH =
        action.payload.mode === Timescale.Day ? DAYWIDTH : WEEKWIDTH
      const UNITFETCHBUFFER =
        action.payload.mode === Timescale.Day ? DAYFETCHBUFFER : WEEKFETCHBUFFER
      const indexGetterFromDate =
        action.payload.mode === Timescale.Day
          ? getDayIndexFromDate
          : getWeekIndexFromDate
      const nearestDateWithData = getNearestDateWithData(
        state.histogramData,
        action.payload.focusedDay
      )

      const focusedDay =
        action.payload.mode === Timescale.Day
          ? nearestDateWithData
          : action.payload.focusedDay
      const focusedDayIndex = indexGetterFromDate(focusedDay, UNITCOUNT)
      const halfUnitsInView = Math.ceil(state.width / UNITWIDTH / 2)

      let offset = state.scrollOffset
      let firstVisibleIndex = state.firstVisibleIndex
      let lastVisibleIndex = state.lastVisibleIndex

      const focusedDayIsInView = isIndexVisible(
        focusedDayIndex,
        state.firstVisibleIndex,
        state.lastVisibleIndex
      )

      if (!focusedDayIsInView) {
        offset = focusedDayIndex * UNITWIDTH - state.width / 2 + UNITWIDTH / 2
        firstVisibleIndex = focusedDayIndex - halfUnitsInView
        lastVisibleIndex = focusedDayIndex + halfUnitsInView
      }

      const largestDayCount = calculateLargestDayCount(
        state.histogramData,
        firstVisibleIndex,
        lastVisibleIndex,
        action.payload.mode
      )

      return {
        ...state,
        activeIndex: focusedDayIndex,
        firstFetchMarker: firstVisibleIndex - UNITFETCHBUFFER,
        firstVisibleIndex,
        focusedDay,
        largestDayCount,
        lastFetchMarker: lastVisibleIndex + UNITFETCHBUFFER,
        lastVisibleIndex,
        scrollOffset: offset,
        targetDay: action.payload.focusedDay,
      }
    }
    case actionTypes.SET_DIMENSIONS: {
      const UNITCOUNT =
        action.payload.mode === Timescale.Day ? DAYCOUNT : WEEKCOUNT
      const UNITWIDTH =
        action.payload.mode === Timescale.Day ? DAYWIDTH : WEEKWIDTH

      // Keep the existing scrollOffset, if possible.
      // SET_DIMENSIONS is triggered when the navigation sidebar collapses from opening the moment shroud.
      // Users expect to see the original histogram position after closing the moment shroud.
      // see https://workwithopal.atlassian.net/browse/SUP-4718)
      const scrollOffset =
        state.scrollOffset ||
        (UNITCOUNT / 2) * UNITWIDTH - action.payload.width / 2 + UNITWIDTH / 2
      const endOffset = scrollOffset + action.payload.width - UNITWIDTH
      const firstVisibleIndex = getIndexFromScrollOffset(
        scrollOffset,
        UNITWIDTH
      )
      const lastVisibleIndex = getIndexFromScrollOffset(endOffset, UNITWIDTH)
      return {
        ...state,
        firstVisibleIndex,
        lastVisibleIndex,
        height: action.payload.height,
        scrollOffset,
        width: action.payload.width,
      }
    }
    case actionTypes.SET_DRAG_OFFSET: {
      const UNITWIDTH =
        action.payload.mode === Timescale.Day ? DAYWIDTH : WEEKWIDTH

      const dragOffset = action.payload.offset
      const dragOffsetDifference = dragOffset - state.dragOffset
      const scrollOffset = state.scrollOffset - dragOffsetDifference

      const firstVisibleIndex = getIndexFromScrollOffset(
        scrollOffset,
        UNITWIDTH
      )
      const lastVisibleIndex = getIndexFromScrollOffset(
        scrollOffset + state.width - UNITWIDTH,
        UNITWIDTH
      )

      const largestDayCount = calculateLargestDayCount(
        state.histogramData,
        firstVisibleIndex,
        lastVisibleIndex,
        action.payload.mode
      )
      return {
        ...state,
        scrollOffset,
        dragOffset,
        firstVisibleIndex,
        largestDayCount,
        lastVisibleIndex,
      }
    }
    case actionTypes.SET_DRAG_END:
      return {
        ...state,
        dragOffset: 0,
        isDragging: false,
        previousScrollOffset: state.scrollOffset,
      }
    case actionTypes.SET_DRAG_START:
      return {
        ...state,
        dragOffset: action.payload,
        isDragging: true,
        previousScrollOffset: state.scrollOffset,
      }
    case actionTypes.SET_FETCH_MARKERS: {
      return {
        ...state,
        firstFetchMarker: action.payload.firstFetchMarker,
        lastFetchMarker: action.payload.lastFetchMarker,
      }
    }
    case actionTypes.SET_HISTOGRAM_DATA: {
      const UNITWIDTH =
        action.payload.mode === Timescale.Day ? DAYWIDTH : WEEKWIDTH

      // When the fetch scope changes (search, mode, filters, duration)
      // the server omits days that no longer carry posts, so we have
      // to drop prior bars instead of merging into them.
      const resetHistogramData =
        action.payload.searchTerm !== state.searchTerm ||
        action.payload.viewMode !== state.viewMode ||
        action.payload.filterScope !== state.filterScope ||
        action.payload.spanInMarketDays !== state.spanInMarketDays
      const data = buildHistogramData(
        action.payload.histogramData,
        state.histogramData,
        resetHistogramData,
        action.payload.viewMode
      )
      const launchingData = buildLaunchingData(
        action.payload.histogramData,
        state.launchingData,
        resetHistogramData,
        action.payload.viewMode
      )

      const firstVisibleIndex = getIndexFromScrollOffset(
        state.scrollOffset,
        UNITWIDTH
      )
      const lastVisibleIndex = getIndexFromScrollOffset(
        state.scrollOffset + state.width - UNITWIDTH,
        UNITWIDTH
      )
      const largestDayCount = calculateLargestDayCount(
        data,
        firstVisibleIndex,
        lastVisibleIndex,
        action.payload.mode
      )

      // The Day strip marks the first day at or after the day asked for that
      // carries content. That mark is first worked out on mount, before any
      // bars have arrived, so there is nothing to search. Work it out again
      // on the data itself, or the mark keeps the day that was asked for.
      const focusedDay =
        action.payload.mode === Timescale.Day && state.targetDay
          ? getNearestDateWithData(data, state.targetDay)
          : state.focusedDay

      return {
        ...state,
        focusedDay,
        histogramData: {
          ...data,
        },
        launchingData,
        largestDayCount,
        searchTerm: action.payload.searchTerm,
        viewMode: action.payload.viewMode,
        filterScope: action.payload.filterScope,
        spanInMarketDays: action.payload.spanInMarketDays,
      }
    }
    case actionTypes.SET_SCROLL_OFFSET: {
      const UNITWIDTH =
        action.payload.mode === Timescale.Day ? DAYWIDTH : WEEKWIDTH
      const firstVisibleIndex = getIndexFromScrollOffset(
        state.scrollOffset - UNITWIDTH,
        UNITWIDTH
      )
      const lastVisibleIndex = getIndexFromScrollOffset(
        state.scrollOffset + state.width - UNITWIDTH,
        UNITWIDTH
      )

      return {
        ...state,
        firstVisibleIndex,
        lastVisibleIndex,
        scrollOffset: action.payload.offset,
      }
    }
    case actionTypes.UPDATE_HISTOGRAM_DATA: {
      const updatedCountsAtDates = {
        // Guard the source day: it may be absent from `histogramData` (e.g. a
        // day the server omitted), and `undefined - 1` would poison the bar
        // with NaN. Clamp at 0 since a count can't go negative.
        [action.payload.fromDate]: Math.max(
          0,
          (state.histogramData[action.payload.fromDate] || 0) - 1
        ),
        [action.payload.toDate]:
          (state.histogramData[action.payload.toDate] || 0) + 1,
      }
      return {
        ...state,
        histogramData: {
          ...state.histogramData,
          ...updatedCountsAtDates,
        },
      }
    }
    default:
      throw new Error('action taken on Histogram that cannot be handled')
  }
}
